import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import { ArrowLeft, Download } from "lucide-react";

import DataTable from "../../components/data-table/DataTable";
import EmptyState from "../../components/data-table/EmptyState";
import StatCard from "../dashboard/components/StatCard";
import { Button } from "../../components/ui/button";
import { API } from "../../constant/api";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import { formatExportDate } from "../../utils/formatters";
import { depositNairaValue } from "../../utils/money";
import { formatAmount } from "../../utils/wordFunctions";
import DepositDetailSheet from "./components/DepositDetailSheet";
import { depositColumns, DepositRow } from "./components/depositColumns";

const PAGE_SIZE = 10;
/** Cap on the one-shot export request, so a busy page can't ask for 50k rows. */
const EXPORT_LIMIT = 1000;

const PaymentPageDetail = () => {
  const { reference = "" } = useParams();
  const { state } = useLocation() as { state?: { name?: string } };
  const axiosInstance = useAuthAxios();

  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<DepositRow | null>(null);

  const {
    data: deposits,
    error,
    isLoading,
    isFetching,
  } = useQuery({
    // reference must be in the key, otherwise opening a second payment page
    // renders the first one's cached deposits.
    queryKey: ["page-deposits", reference, page],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.getPaymentTxn(reference, "", "", PAGE_SIZE, page)
      );
      return response?.data?.data;
    },
    retry: 1,
    enabled: !!reference,
  });

  const totalDeposits = deposits?.pagination?.totalDeposits ?? 0;

  // Second request purely to build the CSV and the page totals.
  const { data: allDeposits } = useQuery({
    queryKey: ["page-deposits-export", reference, totalDeposits],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.getPaymentTxn(
          reference,
          "",
          "",
          Math.min(totalDeposits, EXPORT_LIMIT),
          1
        )
      );
      return response?.data?.data;
    },
    retry: 1,
    enabled: !!reference && totalDeposits > 0,
  });

  useEffect(() => {
    if (error) {
      const err = error as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    }
  }, [error]);

  const rows: DepositRow[] = deposits?.deposits ?? [];
  const allRows: DepositRow[] = useMemo(
    () => allDeposits?.deposits ?? [],
    [allDeposits]
  );

  const totals = useMemo(() => {
    const usd = allRows.reduce((sum, row) => sum + (row.usdtAmount ?? 0), 0);
    const naira = allRows.reduce((sum, row) => sum + depositNairaValue(row), 0);
    return { usd, naira, count: allRows.length };
  }, [allRows]);

  const exportData = useMemo(
    () =>
      allRows.map((row) => ({
        txnid: row.txid ?? "",
        coin: row.currency ?? "",
        network: row.network ?? "",
        toAddress: row.toAddress ?? "",
        amount: formatAmount(row.amount),
        amountInUsd: formatAmount(row.usdtAmount),
        amountInNaira: formatAmount(depositNairaValue(row)),
        dateCreated: formatExportDate(row.processedAt ?? row.createdAt),
      })),
    [allRows]
  );

  const title = state?.name || `Page #${reference}`;

  return (
    <div className="mx-auto w-full max-w-[1400px] font-manrope">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="-ml-2 mb-3 gap-1.5 text-muted-foreground"
      >
        <Link to="/payment">
          <ArrowLeft className="size-4" />
          Back to payments
        </Link>
      </Button>

      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className="min-w-0">
          <h1 className="truncate font-sora text-[24px] font-bold text-foreground">
            {title}
          </h1>
          <p className="mt-1 font-mono text-[13px] font-medium text-muted-foreground">
            #{reference}
          </p>
        </div>

        <CSVLink
          data={exportData}
          filename={`${title}-transactions.csv`}
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Download className="size-4" />
          Export CSV
        </CSVLink>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 mds:grid-cols-3">
        <StatCard
          label="Total received"
          value={`$${formatAmount(totals.usd)}`}
          footer={[`${formatAmount(totals.naira)} NGN`]}
          loading={isLoading}
        />
        <StatCard
          label="Transactions"
          value={String(totalDeposits)}
          loading={isLoading}
        />
        <StatCard
          label="Average payment"
          value={`$${formatAmount(totals.count ? totals.usd / totals.count : 0)}`}
          loading={isLoading}
        />
      </div>

      <div className="mt-8">
        <h2 className="mb-4 text-[18px] font-bold text-foreground">
          Recent activity
        </h2>

        <DataTable<DepositRow>
          columns={depositColumns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          onRowClick={setSelected}
          emptyState={
            <EmptyState
              title="No transactions for this page yet"
              description="Payments made through this link will appear here."
            />
          }
          pagination={{
            page,
            totalPages: deposits?.pagination?.totalPages ?? 0,
            totalItems: totalDeposits,
            onPageChange: setPage,
          }}
        />
      </div>

      <DepositDetailSheet
        deposit={selected}
        open={!!selected}
        onOpenChange={(next) => !next && setSelected(null)}
      />
    </div>
  );
};

export default PaymentPageDetail;
